// If you don't have file-saver, install it: npm install file-saver
import { saveAs } from 'file-saver';

// Helper to merge parent evidence fields into child evidence (fill missing fields as fallback)
function mergeParentFieldsIntoChild(childEv: any, parentEv: any): any {
  const merged = { ...childEv };
  
  // Inherit ALL fields from parent as fallback (if child field is missing, null, or undefined)
  Object.keys(parentEv).forEach(field => {
    // Skip internal fields that shouldn't be inherited
    if (field === '_id' || field === 'parentId' || field === 'isContainer' || field === 'childEvidences') {
      return;
    }
    
    const childValue = merged[field];
    const parentValue = parentEv[field];
    
    // If child doesn't have the field or it's null/undefined, use parent's value
    if (childValue === null || childValue === undefined || childValue === '') {
      if (parentValue !== null && parentValue !== undefined && parentValue !== '') {
        merged[field] = parentValue;
      }
    }
    // For nested objects, merge recursively (child properties take precedence)
    else if (typeof childValue === 'object' && typeof parentValue === 'object' && 
             childValue !== null && parentValue !== null && 
             !Array.isArray(childValue) && !Array.isArray(parentValue)) {
      merged[field] = { ...parentValue, ...childValue };
    }
  });
  
  return merged;
}

// Helper to expand grouped evidences (containers with children)
function expandGroupedEvidences(evidences: any[]): any[] {
  const expanded: any[] = [];
  
  evidences.forEach(ev => {
    if (!ev || !ev._id) return;
    
    // Check if this is a container evidence with children
    if (ev.isContainer === true && ev.childEvidences && Array.isArray(ev.childEvidences) && ev.childEvidences.length > 0) {
      // Expand: replace container with its children, merging parent fields
      ev.childEvidences.forEach((childEv: any) => {
        if (childEv && childEv._id) {
          const mergedChild = mergeParentFieldsIntoChild(childEv, ev);
          expanded.push(mergedChild);
        }
      });
    } else {
      // Regular evidence (not a container or container without children)
      expanded.push(ev);
    }
  });
  
  return expanded;
}

// Helper to get unique evidences by _id
function getUniqueEvidences(evidences: any[]): any[] {
  const map = new Map();
  evidences.forEach(ev => {
    if (ev && ev._id && !map.has(ev._id)) {
      map.set(ev._id, ev);
    }
  });
  return Array.from(map.values());
}

function getTodayString() {
  const d = new Date();
  const month = d.toLocaleString('en-US', { month: 'short' });
  const day = d.getDate();
  const year = d.getFullYear();
  return `${month}-${day}-${year}`;
}

// --- COLUMN DEFINITIONS (from backend) ---
// Columns will be dynamically generated from flattenIdea and flattenEvidence outputs

// --- FLATTEN HELPERS ---

// Recursive flatten helper
function flattenObjectRecursively(
  obj: any,
  prefix = '',
  result: Record<string, any> = {},
  maxDepth = 3,
  currentDepth = 0
): Record<string, any> {
  if (!obj || typeof obj !== 'object' || currentDepth >= maxDepth) {
    return result;
  }

  // Skip arrays of objects (too complex), but handle arrays of primitives
  if (Array.isArray(obj)) {
    // Special handling for excerpts array
    if (obj.length > 0 && obj[0]?.excerpt) {
      const fieldPath = prefix || 'excerpts';
      result[fieldPath] = '"' + obj.map((e: any) => e.excerpt).join('"\n"') + '"';
    }
    return result;
  }

  for (const key of Object.keys(obj)) {
    // Skip internal/metadata fields and ObjectId fields
    if (
      key.startsWith('_') || 
      key === 'accountId' || 
      key === 'ml' || 
      key === 'importInfo' ||
      key === 'comments' ||
      key.endsWith('Id') || // Skip all ID fields (submitterId, ownerId, etc.)
      key === 'buffer' || // Skip MongoDB ObjectId buffer property
      key === 'id' && prefix.endsWith('Id') // Skip id property of ObjectId objects
    ) {
      continue;
    }

    const value = obj[key];
    const fieldPath = prefix ? `${prefix}.${key}` : key;

    // If it's a primitive or date, add it
    if (
      value === null ||
      value === undefined ||
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      value instanceof Date
    ) {
      result[fieldPath] = value;
    } 
    // If it's an object, recurse (but not arrays of objects)
    else if (typeof value === 'object' && !Array.isArray(value)) {
      flattenObjectRecursively(value, fieldPath, result, maxDepth, currentDepth + 1);
    }
    // If it's an array, handle specially
    else if (Array.isArray(value)) {
      flattenObjectRecursively(value, fieldPath, result, maxDepth, currentDepth + 1);
    }
  }

  return result;
}

function flattenIdea(idea: any): Record<string, any> {
  // Start with explicit hardcoded mappings (backwards compatibility)
  const explicitMappings: Record<string, any> = {
    id: idea._id,
    title: idea.title,
    description: idea.description,
    state: idea.state,
    status: idea.status,
    priority: idea.priority,
    domain: idea.domain,
    ownerName: idea.ownerName,
    createdAt: idea.createdAt,
    updatedAt: idea.updatedAt
  };
  
  // Get dynamic flattening to catch everything
  const dynamicFlattened = flattenObjectRecursively(idea);
  
  // Enrich with dynamic fields that aren't already in explicit mappings
  const enrichedFields: Record<string, any> = {};
  for (const [key, value] of Object.entries(dynamicFlattened)) {
    if (!(key in explicitMappings)) {
      enrichedFields[key] = value;
    }
  }

  // Return: explicit mappings first, then additional dynamic fields
  return { ...explicitMappings, ...enrichedFields };
}

function flattenEvidence(ev: any): Record<string, any> {
  // Start with explicit hardcoded mappings (backwards compatibility)
  const explicitMappings: Record<string, any> = {
    // Core fields with standard names
    id: ev._id,
    created_at: ev.createdAt,
    title: ev.title || null,
    description: ev.text || null,
    origin: ev.origin,
    origin_id: ev.originId || null,
    type: ev.type,
    priority: ev.priority,
    business_name: ev.businessName,
    source: ev.sourceOfAssignment,
    status: ev.state,
    
    // Excerpts special formatting
    excerpts: ev?.excerpts?.length > 0 ? '"' + [...ev?.excerpts.map((e: any) => e.excerpt)].join('"\n"') + '"' : null,
    
    // Company fields (explicit paths)
    'company.name': ev.company?.name || null,
    'company.crm_id': ev.company?.crmId || null,
    'company.intercom_id': ev.company?.intercomId || null,
    'company.sf_id': ev.company?.sfId || null,
    'company.zendesk_id': ev.company?.zendeskId || null,
    'company.type': ev.company?.type || null,
    'company.email': ev.company?.companyUser?.originalItem?.email || null,
    
    // Component fields
    domain: ev.componentObj?.domain,
    product_area: ev.componentObj?.name ?? ev.componentObj?.name,
    product_sub_area: ev.componentObj?.ch?.name ? ev.componentObj?.name : null,
    
    // Owner fields
    'owner.name': ev.owner?.name || null,
    'owner.email': ev.owner?.email || null,
    
    // Submitter fields
    'submitter.name': ev.submitter?.name || null,
    'submitter.email': ev.submitter?.email || null,
    
    // Contact fields
    'contact.name': ev.contact?.name || null,
    'contact.email': ev.contact?.email || null,
    
    // CX Item fields (actual field names)
    'cxItem.origin': ev.cxItem?.origin || null,
    'cxItem.originId': ev.cxItem?.originId || null,
    'cxItem.type': ev.cxItem?.type || null,
    'cxItem.title': ev.cxItem?.title || ev.cxItem?.name || null,
    'cxItem.name': ev.cxItem?.name || null,
    'cxItem.link': ev.cxItem?.link || null,

    // Opportunity fields (mirrors cxItem; only populated when evidence.linkedOpportunityId is set)
    'opportunity.title': ev.opportunity?.title || null,
    'opportunity.amount': ev.opportunity?.amount ?? null,
    'opportunity.status': ev.opportunity?.status || null,
    'opportunity.isOpen': ev.opportunity?.isOpen ?? null,
    'opportunity.originId': ev.opportunity?.originId || null,
    'opportunity.origin': ev.opportunity?.origin || null,
    'opportunity.link': ev.opportunity?.link || null,
    'opportunity.company.name': ev.opportunity?.company?.name || null,

    // Origin Object aliases (backwards compatibility)
    'origin_object.origin': ev.cxItem?.origin || null,
    'origin_object.origin_id': ev.cxItem?.originId || null,
    'origin_object.type': ev.cxItem?.type || null,
    'origin_object.title': ev.cxItem?.title || ev.cxItem?.name || null,
    'origin_object.link': ev.cxItem?.link || null
  };
  
  // Get dynamic flattening to catch everything
  const dynamicFlattened = flattenObjectRecursively(ev);
  
  // Enrich with dynamic fields that aren't already in explicit mappings
  const enrichedFields: Record<string, any> = {};
  for (const [key, value] of Object.entries(dynamicFlattened)) {
    if (!(key in explicitMappings)) {
      enrichedFields[key] = value;
    }
  }

  // Return: explicit mappings first, then additional dynamic fields
  return { ...explicitMappings, ...enrichedFields };
}

// Helper to get value from flattened data using field path
function getFieldValue(flatData: Record<string, any>, fieldPath: string): any {
  // Direct access for flat fields
  if (flatData.hasOwnProperty(fieldPath)) {
    return flatData[fieldPath];
  }
  // No value found
  return null;
}

// Main function
export function downloadIdeaToCSV({
  idea,
  allRequests = [],
  matches = [],
  requestsMatches = [],
  filename,
  customConfig,
}: {
  idea: any,
  allRequests: any[],
  matches: any[],
  requestsMatches: any[],
  filename?: string,
  customConfig?: { columns: any[] },
}) {
  // 1. Filename
  const ideaId = idea?._id || 'unknown';
  const dateStr = getTodayString();
  const finalFilename = filename || `Idea_${ideaId}_${dateStr}.csv`;

  // 2. Flatten idea and evidence data
  const ideaFlat = flattenIdea(idea);

  // 3. Evidence filtering and expansion
  const strongMatches = (matches || []).filter((item: any) => item?.match?.level?.toLowerCase() === 'strong');  
  const allEvidences = [...allRequests, ...strongMatches];
  
  // Expand grouped evidences (containers with children)
  const expandedEvidences = expandGroupedEvidences(allEvidences);
  
  // Get unique evidences by _id
  const evidences = getUniqueEvidences(expandedEvidences);

  // 4. Determine columns to use
  let columnsToUse: any[];
  
  if (customConfig && customConfig.columns) {
    // Use custom configuration
    columnsToUse = customConfig.columns
      .filter(col => col.enabled)
      .sort((a, b) => a.order - b.order);
  } else {
    // Use default configuration (backwards compatibility)
    columnsToUse = getDefaultColumns();
  }

  // 5. Build header line
  const headerLine = columnsToUse.map(col => col.label).join(',');

  // 6. Build evidence rows
  const evidenceRows = evidences.map(ev => {
    const flatEvidence = flattenEvidence(ev);
    
    return columnsToUse.map(col => {
      let val: any;
      
      if (col.source === 'idea') {
        val = getFieldValue(ideaFlat, col.fieldPath);
      } else {
        val = getFieldValue(flatEvidence, col.fieldPath);
      }
      
      if (val === undefined || val === null) return '';
      let v = String(val).replace(/"/g, '""');
      if (/[",\n]/.test(v)) v = `"${v}"`;
      return v;
    }).join(',');
  });

  // 7. Compose final CSV
  const csvContent = [
    headerLine,
    ...evidenceRows
  ].join('\n');

  // 8. Download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, finalFilename);
}

// Default columns for backwards compatibility
function getDefaultColumns(): any[] {
  return [
    // Idea columns (locked)
    { id: 'idea-id', label: 'Idea.id', fieldPath: 'id', source: 'idea', enabled: true, locked: true, order: 0 },
    { id: 'idea-title', label: 'Idea.title', fieldPath: 'title', source: 'idea', enabled: true, locked: true, order: 1 },
    
    // Evidence columns
    { id: 'ev-company-name', label: 'company.name', fieldPath: 'company.name', source: 'evidence', enabled: true, locked: false, order: 2 },
    { id: 'ev-company-crm-id', label: 'company.crm_id', fieldPath: 'company.crm_id', source: 'evidence', enabled: true, locked: false, order: 3 },
    { id: 'ev-company-intercom-id', label: 'company.intercom_id', fieldPath: 'company.intercom_id', source: 'evidence', enabled: true, locked: false, order: 4 },
    { id: 'ev-company-sf-id', label: 'company.sf_id', fieldPath: 'company.sf_id', source: 'evidence', enabled: true, locked: false, order: 5 },
    { id: 'ev-company-zendesk-id', label: 'company.zendesk_id', fieldPath: 'company.zendesk_id', source: 'evidence', enabled: true, locked: false, order: 6 },
    { id: 'ev-company-type', label: 'company.type', fieldPath: 'company.type', source: 'evidence', enabled: true, locked: false, order: 7 },
    { id: 'ev-company-email', label: 'company.email', fieldPath: 'company.email', source: 'evidence', enabled: true, locked: false, order: 8 },
    { id: 'ev-id', label: 'id', fieldPath: 'id', source: 'evidence', enabled: true, locked: false, order: 9 },
    { id: 'ev-created-at', label: 'created_at', fieldPath: 'created_at', source: 'evidence', enabled: true, locked: false, order: 10 },
    { id: 'ev-title', label: 'title', fieldPath: 'title', source: 'evidence', enabled: true, locked: false, order: 11 },
    { id: 'ev-description', label: 'description', fieldPath: 'description', source: 'evidence', enabled: true, locked: false, order: 12 },
    { id: 'ev-excerpts', label: 'excerpts', fieldPath: 'excerpts', source: 'evidence', enabled: true, locked: false, order: 13 },
    { id: 'ev-origin', label: 'origin', fieldPath: 'origin', source: 'evidence', enabled: true, locked: false, order: 14 },
    { id: 'ev-origin-id', label: 'origin_id', fieldPath: 'origin_id', source: 'evidence', enabled: true, locked: false, order: 15 },
    { id: 'ev-type', label: 'type', fieldPath: 'type', source: 'evidence', enabled: true, locked: false, order: 16 },
    { id: 'ev-priority', label: 'priority', fieldPath: 'priority', source: 'evidence', enabled: true, locked: false, order: 17 },
    { id: 'ev-domain', label: 'domain', fieldPath: 'domain', source: 'evidence', enabled: true, locked: false, order: 18 },
    { id: 'ev-product-area', label: 'product_area', fieldPath: 'product_area', source: 'evidence', enabled: true, locked: false, order: 19 },
    { id: 'ev-product-sub-area', label: 'product_sub_area', fieldPath: 'product_sub_area', source: 'evidence', enabled: true, locked: false, order: 20 },
    { id: 'ev-business-name', label: 'business_name', fieldPath: 'business_name', source: 'evidence', enabled: true, locked: false, order: 21 },
    { id: 'ev-source', label: 'source', fieldPath: 'source', source: 'evidence', enabled: true, locked: false, order: 22 },
    { id: 'ev-status', label: 'status', fieldPath: 'status', source: 'evidence', enabled: true, locked: false, order: 23 },
    { id: 'ev-owner-name', label: 'owner.name', fieldPath: 'owner.name', source: 'evidence', enabled: true, locked: false, order: 24 },
    { id: 'ev-owner-email', label: 'owner.email', fieldPath: 'owner.email', source: 'evidence', enabled: true, locked: false, order: 25 },
    { id: 'ev-origin-obj-origin', label: 'origin_object.origin', fieldPath: 'origin_object.origin', source: 'evidence', enabled: true, locked: false, order: 26 },
    { id: 'ev-origin-obj-origin-id', label: 'origin_object.origin_id', fieldPath: 'origin_object.origin_id', source: 'evidence', enabled: true, locked: false, order: 27 },
    { id: 'ev-origin-obj-type', label: 'origin_object.type', fieldPath: 'origin_object.type', source: 'evidence', enabled: true, locked: false, order: 28 },
    { id: 'ev-origin-obj-title', label: 'origin_object.title', fieldPath: 'origin_object.title', source: 'evidence', enabled: true, locked: false, order: 29 },
    { id: 'ev-origin-obj-link', label: 'origin_object.link', fieldPath: 'origin_object.link', source: 'evidence', enabled: true, locked: false, order: 30 },
    { id: 'ev-submitter-name', label: 'submitter.name', fieldPath: 'submitter.name', source: 'evidence', enabled: true, locked: false, order: 31 },
    { id: 'ev-submitter-email', label: 'submitter.email', fieldPath: 'submitter.email', source: 'evidence', enabled: true, locked: false, order: 32 },
    { id: 'ev-contact-name', label: 'contact.name', fieldPath: 'contact.name', source: 'evidence', enabled: true, locked: false, order: 33 },
    { id: 'ev-contact-email', label: 'contact.email', fieldPath: 'contact.email', source: 'evidence', enabled: true, locked: false, order: 34 },
  ];
} 